import { useState, useMemo, useEffect } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  selectedThemeSelector,
  pinnedThemesAtom,
  customThemesAtom,
  themesAtom,
  pinnedThemesSelector,
  selectedThemeAtom,
  productsAtom,
  selectedProductsAtom,
} from 'atoms'
import { PrintPackageHeader } from './'
import { useSetToast } from 'hooks'
import {
  CardSelector,
  SearchBar,
  Button,
  Tabs,
  NewThemeModal,
  CustomThemeEditor,
  FullscreenModal,
} from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useTranslation } from 'react-i18next'
import { getItemDisplayName } from 'utils/global/getItemDisplayName'
import { createPinnedThemes, deletePinnedThemes } from '@/api/themes'
import { createCustomTheme, getCustomThemes } from '@/api/customThemes'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { M3 } from '@messenger/consts'
import {
  getThemeAppType,
  getOrderProductAppTypes,
  isCrossAppTheme,
} from 'utils/global/isCrossAppTheme'
import { ThemeAppMismatchModal } from './ThemeAppMismatchModal/ThemeAppMismatchModal'

export const Theme = ({ caseInfo }) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('Theme')
  const [search, setSearch] = useState('')
  const [showNewThemeModal, setShowNewThemeModal] = useState(false)
  const [showEditThemeModal, setShowEditThemeModal] = useState(false)
  const [newTheme, setNewTheme] = useState(null)
  const [selectedTheme, setSelectedTheme] = useRecoilState(
    selectedThemeSelector,
  )
  const setSelectedThemeAtom = useSetRecoilState(selectedThemeAtom)
  const themes = useRecoilValue(themesAtom)
  const pinnedThemes = useRecoilValue(pinnedThemesAtom)
  const [customThemes, setCustomThemes] = useRecoilState(customThemesAtom)
  const [activeTab, setActiveTab] = useState(null)
  const setThisPinnedTheme = useSetRecoilState(pinnedThemesSelector)
  const setToast = useSetToast()
  const isAdmin = useIsAdmin()
  const products = useRecoilValue(productsAtom)
  const selectedProducts = useRecoilValue(selectedProductsAtom)
  const [pendingTheme, setPendingTheme] = useState(null)
  const [showMismatchModal, setShowMismatchModal] = useState(false)

  // The order's app type(s) derive from its products' source tables — admin
  // master orders are MDL orders that can carry M3 products, so product
  // source is the only reliable signal. While products are still loading the
  // list is empty and the guard stays silent (isCrossAppTheme never warns on
  // an empty list).
  const orderAppTypes = useMemo(
    () => getOrderProductAppTypes(selectedProducts, products),
    [selectedProducts, products],
  )

  const themeTag = isAdmin
    ? (card) => ({
        label: card.application === M3 ? 'M3' : 'MDL',
      })
    : null

  // Guard against accidentally applying a theme from a different app (e.g. an
  // M3 theme on an MDL order). On a mismatch we defer the change and surface a
  // confirmation modal so no theme request fires until the user confirms.
  const handleSelectTheme = (theme) => {
    if (!theme) {
      setSelectedTheme(null)
      return
    }
    if (isCrossAppTheme(theme, orderAppTypes)) {
      setPendingTheme(theme)
      setShowMismatchModal(true)
      return
    }
    setSelectedTheme(theme)
  }

  const confirmThemeSelection = () => {
    if (pendingTheme) setSelectedTheme(pendingTheme)
    setShowMismatchModal(false)
    setPendingTheme(null)
  }

  const cancelThemeSelection = () => {
    setShowMismatchModal(false)
    setPendingTheme(null)
  }

  useEffect(() => {
    setSelectedThemeAtom(null)
    //eslint-disable-next-line
  }, [])

  const concatThemeArrays = () => {
    const pinnedThemesCategory = []
    const customThemesCategory = []

    let themesWithPinsParsed = [...themes]
    if (pinnedThemes.length > 0) {
      pinnedThemesCategory.push({
        name: 'Pinned Themes',
        themes: pinnedThemes,
      })
    }
    themesWithPinsParsed = themesWithPinsParsed.map((category) => {
      const theseThemes = category.themes.map((theme) => {
        const isPinned = pinnedThemes.find(
          (pinned) => pinned.theme_id === theme.id,
        )
        const obj = { ...theme, isPinned: isPinned?.theme_id ? true : false }
        return obj
      })
      const obj = { ...category, themes: theseThemes }
      return obj
    })

    if (customThemes.length > 0) {
      customThemesCategory.push({
        name: 'Custom Themes',
        themes: customThemes,
      })
    }

    const filteredThemes = pinnedThemesCategory
      .concat(customThemesCategory)
      .concat(themesWithPinsParsed)
    return filteredThemes
  }

  const filteredCategories = useMemo(() => {
    const allCategorizedThemes = concatThemeArrays()
    if (search.trim() === '') {
      if (!activeTab && allCategorizedThemes.length > 0) {
        setActiveTab(allCategorizedThemes[0]?.name)
      }
      return allCategorizedThemes
    } else {
      const searchFilteredCategories = []
      const searchRegex = new RegExp(`${search}`, 'ig')
      allCategorizedThemes.forEach((category) => {
        const newCategory = { ...category }
        newCategory.themes = category.themes.filter((theme) => {
          const displayName = getItemDisplayName(theme.name, 'themes', t)
          return searchRegex.test(displayName)
        })
        if (newCategory.themes.length > 0) {
          searchFilteredCategories.push(newCategory)
        }
      })
      if (!activeTab && searchFilteredCategories.length > 0) {
        setActiveTab(searchFilteredCategories[0]?.name)
      }
      return searchFilteredCategories
    }
    //eslint-disable-next-line
  }, [themes, pinnedThemes, customThemes, search, activeTab])

  const pinTheme = async (e, theme) => {
    try {
      e.stopPropagation()
      const index = pinnedThemes.findIndex(
        (item) => item.id === theme.id || item.theme_id === theme.id,
      )
      const pinnedTheme = pinnedThemes.find(
        (item) => item.id === theme.id || item.theme_id === theme.id,
      )
      const payload = {
        themeId: theme.id,
      }
      if (index < 0) {
        const { data } = await createPinnedThemes(payload)
        data.image = theme.image
        data.name = theme.name

        setThisPinnedTheme(data)
      } else {
        await deletePinnedThemes(pinnedTheme.id)
        setThisPinnedTheme(pinnedTheme)
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const createTheme = async (payload) => {
    try {
      const resp = await createCustomTheme(payload)
      if (resp.status === 201) {
        setToast({
          text: t('toast.themeCreated'),
          icon: 'check',
        })
        setNewTheme(resp.data)
        setShowEditThemeModal(true)
      }
    } catch (err) {
      handleError(
        err?.response?.data?.message || err.message,
        err,
        'deleteOutline',
      )
    }
  }

  const fetchAndUpdateCustomThemes = async () => {
    try {
      const { data } = await getCustomThemes()
      setCustomThemes(data)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const closeEditThemeModal = async () => {
    await fetchAndUpdateCustomThemes()
    setActiveTab('Custom Themes')
    setShowEditThemeModal(false)
  }

  useEffect(() => {
    fetchAndUpdateCustomThemes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PrintPackageHeader
        title={t('package.navigationSteps.chooseTheme')}
        caseInfo={caseInfo}
      />
      <Flex justify="center" align="center" padding="0 40px">
        <Text as="h2">
          {selectedTheme
            ? t('package.theme.selected', {
                name: getItemDisplayName(selectedTheme?.name, 'themes', t),
              })
            : t('package.theme.select')}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center" padding="32px 40px">
        <SearchBar
          placeholder={t('package.theme.searchPlaceholder')}
          value={[search, setSearch]}
          width="600px"
        />

        <Button icon="plus" primary onClick={() => setShowNewThemeModal(true)}>
          {t('package.theme.newThemeButton')}
        </Button>
        <FullscreenModal show={[showNewThemeModal, setShowNewThemeModal]}>
          <NewThemeModal
            createTheme={createTheme}
            setShowNewThemeModal={setShowNewThemeModal}
          />
        </FullscreenModal>
        <FullscreenModal show={[showEditThemeModal, setShowEditThemeModal]}>
          <CustomThemeEditor
            theme={newTheme}
            closeModal={closeEditThemeModal}
          />
        </FullscreenModal>
      </Flex>

      <Tabs
        width="250px"
        tabs={filteredCategories.map((category) => {
          return {
            title: category.name,
            content: (
              <CardSelector
                padding="16px 40px 40px 4px"
                cardAlign="flex-start"
                cardJustify="flex-start"
                cards={category.themes}
                cardWidth="328px"
                cardHeight="auto"
                minHeight="204px"
                cardPadding="16px 24px 16px"
                imageHeight="150px"
                imageWidth="250px"
                imageFit="cover"
                imagePosition="50% 95%"
                height="500px"
                selectedState={[selectedTheme, handleSelectTheme]}
                prependURL="https://bassmollett-theme.imgix.net/"
                cardIcon={{
                  icon: 'thumbtack',
                  clickHandler: pinTheme,
                }}
                cardTag={themeTag}
                iconHover={category.name !== 'Pinned Themes'}
                type="themes"
              />
            ),
          }
        })}
        selected={[activeTab, setActiveTab]}
      />

      <ThemeAppMismatchModal
        show={showMismatchModal}
        currentApp={orderAppTypes.join(' / ')}
        selectedApp={getThemeAppType(pendingTheme)}
        onClose={cancelThemeSelection}
        onConfirm={confirmThemeSelection}
      />
    </>
  )
}

export default Theme
